import { LiveAnnouncer } from "@angular/cdk/a11y";
import { Overlay, OverlayConfig, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal, ComponentType, TemplatePortal } from "@angular/cdk/portal";
import { ComponentRef, EmbeddedViewRef, inject, Injectable, InjectionToken, Injector, OnDestroy, TemplateRef } from "@angular/core";
import { VDS_ALERT_DATA, VdsAlertConfig } from "@dcc/vestas-design-system/angular/components/alert/domain";
import { VdsAlertContainerComponent } from "./alert-container";
import { VdsAlertRef } from "./alert-ref";
import { DefaultAlertComponent } from "./default-alert.component";

export function VO_ALERT_DEFAULT_OPTIONS_FACTORY(): VdsAlertConfig {
    return new VdsAlertConfig();
}

/** Injection token that can be used to specify default alert. */
export const VDS_ALERT_DEFAULT_OPTIONS = new InjectionToken<VdsAlertConfig>("vds-alert-default-options", {
    providedIn: "root",
    factory: VO_ALERT_DEFAULT_OPTIONS_FACTORY
});

@Injectable({ providedIn: "root" })
export class VdsAlert implements OnDestroy {
    private _overlay = inject(Overlay);
    private _live = inject(LiveAnnouncer);
    private _injector = inject(Injector);
    private _parentAlert = inject(VdsAlert, { optional: true, skipSelf: true });
    private _defaultConfig = inject<VdsAlertConfig>(VDS_ALERT_DEFAULT_OPTIONS);

    /**
     * Reference to the current alert in the view *at this level* (in the Angular injector tree).
     * If there is a parent alert service, all operations should delegate to that parent
     * via `_openedAlertRef`.
     */
    private _alertRefAtThisLevel: VdsAlertRef<any> | null = null;

    /** The component that should be rendered as the alerts default component. */
    public simpleAlertComponent = DefaultAlertComponent;

    /** The container component that attaches the provided template or component. */
    public alertContainerComponent = VdsAlertContainerComponent;

    /** Reference to the currently opened alert at *any* level. */
    get _openedAlertRef(): VdsAlertRef<any> | null {
        const parent = this._parentAlert;
        return parent ? parent._openedAlertRef : this._alertRefAtThisLevel;
    }

    set _openedAlertRef(value: VdsAlertRef<any> | null) {
        if (this._parentAlert) {
            this._parentAlert._openedAlertRef = value;
        } else {
            this._alertRefAtThisLevel = value;
        }
    }

    /**
     * Creates and dispatches a alert with a custom component for the content, removing any
     * currently opened alerts.
     *
     * @param component Component to be instantiated.
     * @param config Extra configuration for the alert.
     */
    openFromComponent<T, D = any>(component: ComponentType<T>, config?: VdsAlertConfig<D>): VdsAlertRef<T> {
        return this._attach(component, config) as VdsAlertRef<T>;
    }

    /**
     * Creates and dispatches a alert with a custom template for the content, removing any
     * currently opened alerts.
     *
     * @param template Template to be instantiated.
     * @param config Extra configuration for the alert.
     */
    openFromTemplate(template: TemplateRef<any>, config?: VdsAlertConfig): VdsAlertRef<EmbeddedViewRef<any>> {
        return this._attach(template, config);
    }

    /**
     * Opens a alert with a message and an optional action.
     * @param title The title to show in the alert.
     * @param message The message to show in the alert.
     * @param action The label for the alert action.
     * @param config Additional configuration options for the alert.
     */
    open(title: string | undefined | null, message: string, action = "", config?: VdsAlertConfig): VdsAlertRef<DefaultAlertComponent> {
        const _config = { ...this._defaultConfig, ...config };

        // Since the user doesn't have access to the component, we can
        // override the data to pass in our own message and action.
        _config.data = { title, message, action };
        // Since the alert has `role="alert"`, we don't
        // want to announce the same message twice.
        if (_config.announcementMessage === message) {
            _config.announcementMessage = undefined;
        }

        return this.openFromComponent(this.simpleAlertComponent, _config);
    }

    /**
     * Dismisses the currently-visible alert.
     */
    dismiss(): void {
        if (this._openedAlertRef) {
            this._openedAlertRef.dismiss();
        }
    }

    ngOnDestroy() {
        // Only dismiss the alert at the current level on destroy.
        if (this._alertRefAtThisLevel) {
            this._alertRefAtThisLevel.dismiss();
        }
    }

    /**
     * Attaches the alert container component to the overlay.
     */
    private _attachAlertContainer(overlayRef: OverlayRef, config: VdsAlertConfig): VdsAlertContainerComponent {
        const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;
        const injector = Injector.create({
            parent: userInjector || this._injector,
            providers: [{ provide: VdsAlertConfig, useValue: config }]
        });

        const containerPortal = new ComponentPortal(this.alertContainerComponent, config.viewContainerRef, injector);
        const containerRef: ComponentRef<VdsAlertContainerComponent> = overlayRef.attach(containerPortal);
        containerRef.instance.alertConfig = config;
        return containerRef.instance;
    }

    /**
     * Places a new component or a template as the content of the alert container.
     */
    private _attach<T>(content: ComponentType<T> | TemplateRef<T>, userConfig?: VdsAlertConfig): VdsAlertRef<T | EmbeddedViewRef<any>> {
        const config = { ...new VdsAlertConfig(), ...this._defaultConfig, ...userConfig };
        const overlayRef = this._createOverlay(config);
        const container = this._attachAlertContainer(overlayRef, config);
        const alertRef = new VdsAlertRef<T | EmbeddedViewRef<any>>(container, overlayRef);

        if (content instanceof TemplateRef) {
            const portal = new TemplatePortal(content, null!, {
                $implicit: config.data,
                alertRef
            } as any);

            alertRef.instance = container.attachTemplatePortal(portal);
        } else {
            const injector = this._createInjector(config, alertRef);
            const portal = new ComponentPortal(content, undefined, injector);
            const contentRef = container.attachComponentPortal<T>(portal);

            // We can't pass this via the injector, because the injector is created earlier.
            alertRef.instance = contentRef.instance;
        }

        overlayRef.overlayElement.classList.add("vds-alert-container--fullwidth");

        if (config.announcementMessage) {
            // Wait until the snack bar contents have been announced then deliver this message.
            container._onAnnounce.subscribe(() => {
                this._live.announce(config.announcementMessage!, config.politeness);
            });
        }

        this._animateAlert(alertRef, config);
        this._openedAlertRef = alertRef;
        return this._openedAlertRef;
    }

    /** Animates the old alert out and the new one in. */
    private _animateAlert(alertRef: VdsAlertRef<any>, config: VdsAlertConfig) {
        // When the alert is dismissed, clear the reference to it.
        alertRef.afterDismissed().subscribe(() => {
            // Clear the alert ref if it hasn't already been replaced by a newer alert.
            if (this._openedAlertRef == alertRef) {
                this._openedAlertRef = null;
            }

            if (config.announcementMessage) {
                this._live.clear();
            }
        });

        if (this._openedAlertRef) {
            // If a alert is already in view, dismiss it and enter the
            // new alert after exit animation is complete.
            this._openedAlertRef.afterDismissed().subscribe(() => {
                alertRef.containerInstance.enter();
            });
            this._openedAlertRef.dismiss();
        } else {
            // If no alert is in view, enter the new alert.
            alertRef.containerInstance.enter();
        }

        // If a dismiss timeout is provided, set up dismiss based on after the alert is opened.
        if (config.duration && config.duration > 0) {
            alertRef.afterOpened().subscribe(() => {
                alertRef._dismissAfter(config.duration!);
            });
        }
    }

    /**
     * Creates a new overlay and places it in the correct location.
     * @param config The user-specified alert config.
     */
    private _createOverlay(config: VdsAlertConfig): OverlayRef {
        const overlayConfig = new OverlayConfig();
        overlayConfig.direction = config.direction;

        const positionStrategy = this._overlay.position().global();

        positionStrategy.centerHorizontally();
        // Set horizontal position.
        if (config.verticalPosition === "top") {
            positionStrategy.top("0");
        } else {
            positionStrategy.bottom("0");
        }

        overlayConfig.positionStrategy = positionStrategy;
        return this._overlay.create(overlayConfig);
    }

    /**
     * Creates an injector to be used inside of a alert component.
     * @param config Config that was used to create the alert.
     * @param alertRef Reference to the alert.
     */
    private _createInjector<T>(config: VdsAlertConfig, alertRef: VdsAlertRef<T>): Injector {
        const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;

        return Injector.create({
            parent: userInjector || this._injector,
            providers: [
                { provide: VdsAlertRef, useValue: alertRef },
                { provide: VDS_ALERT_DATA, useValue: config.data }
            ]
        });
    }
}
